import { isResearchRecord, recordFlags } from "./collection.js";
function cleanValue(value, fallback = "Não informado") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function parsePayload(document) {
  if (document.payload_json && typeof document.payload_json === "object") return document.payload_json;
  try { return JSON.parse(document.payload_json || "{}"); }
  catch { return {}; }
}

function mean(values) {
  const valid = values.map(Number).filter(Number.isFinite);
  if (!valid.length) return null;
  return Math.round((valid.reduce((sum, value) => sum + value, 0) / valid.length) * 100) / 100;
}

function normalizePhase(phase) {
  const value = String(phase || "").trim().toLowerCase();
  if (value === "pd_antes") return "pd_antes";
  if (["pd_durante", "pr_durante"].includes(value)) return "pd_durante";
  if (value === "pd_depois") return "pd_depois";
  return "não_informada";
}

function distribution(values) {
  const counts = {};
  values.forEach(value => {
    const key = cleanValue(value);
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([value, count]) => ({ value, count }))
    .sort((left, right) => right.count - left.count || left.value.localeCompare(right.value, "pt-BR"));
}

function chronologicalDistribution(values) {
  const counts = {};
  values.filter(Boolean).forEach(value => {
    const key = String(value).slice(0, 10);
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([value, count]) => ({ value, count }))
    .sort((left, right) => left.value.localeCompare(right.value));
}

function ageDistribution(ages) {
  const bands = [
    { value: "Até 17", min: 0, max: 17 },
    { value: "18–24", min: 18, max: 24 },
    { value: "25–34", min: 25, max: 34 },
    { value: "35–44", min: 35, max: 44 },
    { value: "45–59", min: 45, max: 59 },
    { value: "60+", min: 60, max: 120 }
  ];
  return bands.map(band => ({
    value: band.value,
    count: ages.filter(age => age >= band.min && age <= band.max).length
  }));
}

function isComplete(document) {
  if (!document.finished_at) return false;
  try {
    const payload = parsePayload(document);
    const responses = payload.responses || {};
    const tasks = payload.designSnapshot?.tasks || [];
    return Boolean(
      responses.profile?.gender_identity &&
      Number.isFinite(Number(responses.profile?.age_years)) &&
      responses.mobility?.metro_habit &&
      responses.mobility?.origin_neighborhood &&
      tasks.length > 0 &&
      tasks.every(task => responses.choices?.[task.task_id]?.selected_alternative) &&
      Object.entries(payload.questionnaireSnapshot?.psychometrics || {})
        .filter(([, section]) => Array.isArray(section?.items))
        .every(([kind, section]) => Object.keys(responses.psychometrics?.[kind] || {}).length >= section.items.length)
    );
  } catch {
    return false;
  }
}

function localFinishedDate(document) {
  if (!document.finished_at) return null;
  try {
    const payload = parsePayload(document);
    const offsetMinutes = Number.isFinite(Number(payload.timezoneOffset)) ? Number(payload.timezoneOffset) : 180;
    return new Date(new Date(document.finished_at).getTime() - offsetMinutes * 60_000).toISOString().slice(0, 10);
  } catch {
    return String(document.finished_at).slice(0, 10);
  }
}

function researcherSummary(records, researchers = []) {
  const fieldResearchers = researchers.filter(researcher => !researcher.role || researcher.role === "researcher");
  const directory = new Map(fieldResearchers.map(researcher => [researcher.id, researcher]));
  const ids = new Set([...fieldResearchers.map(researcher => researcher.id), ...records.map(record => record.researcherId)]);
  return [...ids].filter(Boolean).map(id => {
    const researcher = directory.get(id);
    const own = records.filter(record => record.researcherId === id);
    const dates = own.map(record => record.finishedAt || record.receivedAt).filter(Boolean).sort();
    return {
      id,
      name: researcher?.name || own.find(record => record.researcherName)?.researcherName || "Pesquisador",
      email: researcher?.email || own.find(record => record.researcherEmail)?.researcherEmail || "E-mail não registrado",
      active: researcher?.status !== false,
      must_change_password: Boolean(researcher?.mustChangePassword),
      synchronized: own.filter(record => !record.excluded).length,
      valid: own.filter(record => !record.deleted && !record.excluded).length,
      complete: own.filter(record => record.complete && !record.excluded && !record.deleted).length,
      deletion_requests: own.filter(record => record.deleted).length,
      devices: [...new Set(own.map(record => record.device).filter(Boolean))],
      latest_at: dates.at(-1) || null
    };
  }).sort((left, right) => right.synchronized - left.synchronized || left.name.localeCompare(right.name, "pt-BR"));
}

function aggregate(documents, researchers = []) {
  const records = documents.map(document => ({
    phase: normalizePhase(document.phase),
    deleted: Boolean(document.deletion_requested),
    excluded: !isResearchRecord(document),
    ...recordFlags(document),
    complete: isComplete(document),
    gender: cleanValue(document.gender_identity),
    age: Number.isFinite(Number(document.age_years)) ? Number(document.age_years) : null,
    metroHabit: cleanValue(document.metro_habit),
    treatmentGroup: cleanValue(
      document.treatment_group || parsePayload(document).responses?.field_context?.treatment_group
    ),
    city: cleanValue(document.origin_city),
    neighborhood: cleanValue(document.origin_neighborhood),
    interviewer: cleanValue(document.interviewer_code),
    device: cleanValue(document.device_id, ""),
    researcherId: cleanValue(document.synced_by, ""),
    researcherEmail: cleanValue(document.researcher_email, ""),
    researcherName: cleanValue(document.researcher_name, ""),
    finishedAt: document.finished_at || null,
    finishedDay: localFinishedDate(document),
    receivedAt: document.$updatedAt || null
  }));
  const valid = records.filter(record => !record.deleted && !record.excluded);
  const ages = valid.map(record => record.age).filter(age => Number.isFinite(age) && age >= 0 && age <= 120);
  const dates = records.map(record => record.receivedAt).filter(Boolean).sort();

  return {
    totals: {
      executed: records.filter(record => !record.excluded).length,
      synchronized: records.length,
      complete: records.filter(record => record.complete && !record.excluded && !record.deleted).length,
      deletion_requests: records.filter(record => record.deleted).length,
      tests: records.filter(record => record.is_test).length,
      duplicate_attempts: records.filter(record => record.duplicate_attempt).length,
      valid: valid.length
    },
    latest_received_at: dates.at(-1) || null,
    age_summary: {
      answered: ages.length,
      mean: ages.length ? Math.round((ages.reduce((sum, age) => sum + age, 0) / ages.length) * 10) / 10 : null,
      min: ages.length ? Math.min(...ages) : null,
      max: ages.length ? Math.max(...ages) : null
    },
    distributions: {
      daily: chronologicalDistribution(valid.map(record => record.finishedDay)),
      gender: distribution(valid.map(record => record.gender)),
      age_band: ageDistribution(ages),
      metro_habit: distribution(valid.map(record => record.metroHabit)),
      treatment_group: distribution(valid.map(record => record.treatmentGroup)),
      city: distribution(valid.map(record => record.city)),
      neighborhood: distribution(valid.map(record => record.neighborhood)),
      interviewer: distribution(valid.map(record => record.interviewer)),
      device: distribution(valid.map(record => record.device))
    },
    researchers: researcherSummary(records, researchers),
    data_quality: {
      incomplete: records.filter(record => !record.complete).length,
      missing_gender: valid.filter(record => record.gender === "Não informado").length,
      missing_or_invalid_age: valid.length - ages.length,
      missing_city: valid.filter(record => record.city === "Não informado").length,
      missing_neighborhood: valid.filter(record => record.neighborhood === "Não informado").length,
      missing_treatment_group: valid.filter(record => record.treatmentGroup === "Não informado").length,
      unknown_phase: records.filter(record => record.phase === "não_informada").length
    }
  };
}

export function buildAdminDashboard(documents, researchers = []) {
  const phases = ["pd_antes", "pd_durante", "pd_depois"];
  return {
    schema_version: "3.0.0",
    source: "supabase_postgres",
    generated_at: new Date().toISOString(),
    overall: aggregate(documents, researchers),
    by_phase: Object.fromEntries(phases.map(phase => [
      phase,
      aggregate(documents.filter(document => normalizePhase(document.phase) === phase), researchers)
    ]))
  };
}

function choiceGroup(busCount, metroCount) {
  if (busCount > metroCount) return "bus";
  if (metroCount > busCount) return "metro";
  return "tie";
}

function sortedLevels(levels) {
  return [...levels].sort((left, right) => {
    const leftNumber = Number(left.value);
    const rightNumber = Number(right.value);
    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) return leftNumber - rightNumber;
    return String(left.value).localeCompare(String(right.value), "pt-BR");
  });
}

export function buildChoiceAnalytics(documents, filters = {}) {
  const phase = filters.phase && filters.phase !== "all" ? normalizePhase(filters.phase) : null;
  const gender = filters.gender && filters.gender !== "all" ? String(filters.gender) : null;
  const treatmentGroup = filters.treatmentGroup && filters.treatmentGroup !== "all"
    ? String(filters.treatmentGroup)
    : null;
  const questionnaireVersion = filters.questionnaireVersion;
  const validDocuments = documents.filter(document => (
    isResearchRecord(document) &&
    !document.deletion_requested
    && (!questionnaireVersion || Number(document.questionnaire_version || parsePayload(document).questionnaireSnapshot?.questionnaire_version) === Number(questionnaireVersion))
    && (!phase || normalizePhase(document.phase) === phase)
    && (!gender || document.gender_identity === gender)
    && (!treatmentGroup || (
      document.treatment_group || parsePayload(document).responses?.field_context?.treatment_group
    ) === treatmentGroup)
  ));

  const choiceCounts = { bus: 0, metro: 0 };
  const genderChoices = new Map();
  const attributeMap = new Map();
  const psychometricItems = new Map();
  const profileGroups = new Map();
  let decisionCount = 0;

  for (const document of validDocuments) {
    const payload = parsePayload(document);
    const responses = payload.responses || {};
    const tasks = payload.designSnapshot?.tasks || [];
    const interviewGender = document.gender_identity || responses.profile?.gender_identity || "Não informado";
    const genderEntry = genderChoices.get(interviewGender) || { value: interviewGender, bus: 0, metro: 0, total: 0 };
    let interviewBus = 0;
    let interviewMetro = 0;

    for (const task of tasks) {
      const selected = responses.choices?.[task.task_id]?.selected_alternative;
      if (!["bus", "metro"].includes(selected)) continue;
      decisionCount += 1;
      choiceCounts[selected] += 1;
      genderEntry[selected] += 1;
      genderEntry.total += 1;
      if (selected === "bus") interviewBus += 1;
      else interviewMetro += 1;

      for (const alternative of task.alternatives || []) {
        for (const attribute of alternative.attributes || []) {
          const attributeEntry = attributeMap.get(attribute.id) || {
            id: attribute.id,
            label: attribute.label.replace(/transbordo/gi, "integração"),
            unit: attribute.unit || null,
            alternative: alternative.id,
            levels: new Map()
          };
          const levelKey = String(attribute.value);
          const level = attributeEntry.levels.get(levelKey) || {
            value: attribute.value,
            display_value: attribute.display_value || String(attribute.value),
            count: 0,
            metro: 0
          };
          level.count += 1;
          if (selected === "metro") level.metro += 1;
          attributeEntry.levels.set(levelKey, level);
          attributeMap.set(attribute.id, attributeEntry);
        }
      }
    }
    genderChoices.set(interviewGender, genderEntry);

    const questionnaire = payload.questionnaireSnapshot?.psychometrics || {};
    const interviewScores = {};
    for (const [kind, section] of Object.entries(questionnaire)) {
      if (!Array.isArray(section?.items)) continue;
      const answers = responses.psychometrics?.[kind] || {};
      const adjustedScores = [];
      for (const item of section.items || []) {
        const raw = Number(answers[item.id]);
        if (!Number.isFinite(raw)) continue;
        const adjusted = item.reverse_scored ? 6 - raw : raw;
        adjustedScores.push(adjusted);
        const entry = psychometricItems.get(item.id) || {
          id: item.id,
          kind,
          title: section.title,
          text: item.text,
          reverse_scored: Boolean(item.reverse_scored),
          raw: [],
          adjusted: []
        };
        entry.raw.push(raw);
        entry.adjusted.push(adjusted);
        psychometricItems.set(item.id, entry);
      }
      interviewScores[kind] = mean(adjustedScores);
    }

    const group = choiceGroup(interviewBus, interviewMetro);
    const profile = profileGroups.get(group) || {
      value: group,
      interviews: 0,
      security: [],
      comfort: [],
      trust: []
    };
    profile.interviews += 1;
    if (interviewScores.security != null) profile.security.push(interviewScores.security);
    if (interviewScores.comfort != null) profile.comfort.push(interviewScores.comfort);
    if (interviewScores.trust != null) profile.trust.push(interviewScores.trust);
    profileGroups.set(group, profile);
  }

  const psychometricKinds = [...new Set([...psychometricItems.values()].map(item => item.kind))];
  const psychometrics = Object.fromEntries(psychometricKinds.map(kind => {
    const items = [...psychometricItems.values()]
      .filter(item => item.kind === kind)
      .sort((left, right) => left.id.localeCompare(right.id))
      .map(item => ({
        id: item.id,
        text: item.text,
        reverse_scored: item.reverse_scored,
        count: item.raw.length,
        raw_mean: mean(item.raw),
        adjusted_mean: mean(item.adjusted)
      }));
    return [kind, {
      title: [...psychometricItems.values()].find(item => item.kind === kind)?.title,
      mean: mean(items.flatMap(item => item.adjusted_mean == null ? [] : [item.adjusted_mean])),
      items
    }];
  }));

  return {
    filters: { phase: phase || "all", gender: gender || "all", treatmentGroup: treatmentGroup || "all" },
    interviews: validDocuments.length,
    decisions: decisionCount,
    choices: [
      { value: "bus", count: choiceCounts.bus },
      { value: "metro", count: choiceCounts.metro }
    ],
    choice_by_gender: [...genderChoices.values()]
      .filter(item => item.total > 0)
      .sort((left, right) => right.total - left.total || left.value.localeCompare(right.value, "pt-BR")),
    psychometrics,
    psychometric_by_choice: [...profileGroups.values()]
      .map(group => ({
        value: group.value,
        interviews: group.interviews,
        security_mean: mean(group.security),
        comfort_mean: mean(group.comfort),
        trust_mean: mean(group.trust)
      }))
      .sort((left, right) => right.interviews - left.interviews),
    attributes: [...attributeMap.values()].map(attribute => ({
      id: attribute.id,
      label: attribute.label.replace(/transbordo/gi, "integração"),
      unit: attribute.unit,
      alternative: attribute.alternative,
      levels: sortedLevels([...attribute.levels.values()].map(level => ({
        ...level,
        metro_share: level.count ? Math.round((level.metro / level.count) * 1000) / 10 : 0
      })))
    }))
  };
}
